import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Plus, Search, DollarSign, Filter, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
import {
  getCatalogItems,
  updateCatalogItem
} from '../utils/billingApi';
import { CatalogItem } from '../types/billing';
import ConfirmDialog from '../components/common/ConfirmDialog';

const CatalogManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Get category from URL, default to 'All'
  const selectedCategory = searchParams.get('category') || 'All';
  
  const setSelectedCategory = (category: string) => {
    if (category === 'All') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [itemToToggle, setItemToToggle] = useState<CatalogItem | null>(null);
  const [showInactive, setShowInactive] = useState(() => {
    return localStorage.getItem('catalog_showInactive') === 'true';
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  useEscapeKey(() => {
    setShowAddMenu(false);
    setShowFilterMenu(false);
  }, showAddMenu || showFilterMenu);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (showAddMenu && addMenuRef.current && !addMenuRef.current.contains(target)) {
        setShowAddMenu(false);
      }

      if (showFilterMenu && filterMenuRef.current && !filterMenuRef.current.contains(target)) {
        setShowFilterMenu(false);
      }
    };
    // Use capture so this still runs even if inner elements call stopPropagation()
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showAddMenu, showFilterMenu]);

  // Persist filter preference
  const handleShowInactiveChange = (value: boolean) => {
    setShowInactive(value);
    localStorage.setItem('catalog_showInactive', String(value));
  };

  const categories = ['All', 'Diagnostic', 'Treatment', 'Medication', 'Product', 'Labs', 'Immunizations'];

  useEffect(() => {
    if (user?.practice_id) {
      fetchItems();
    }
  }, [user?.practice_id]);

  useEffect(() => {
    filterItems();
  }, [items, selectedCategory, searchTerm, showInactive]);

  const fetchItems = async () => {
    if (!user?.practice_id) return;

    setLoading(true);
    try {
      const data = await getCatalogItems(user.practice_id, undefined, undefined, false); // Get all items including inactive
      setItems(data);
    } catch (err: any) {
      console.error('Failed to fetch catalog:', err);
      alert('Failed to load catalog items: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    // Filter by active status (hide inactive unless toggle is on)
    if (!showInactive) {
      filtered = filtered.filter(item => item.is_active);
    }

    // Filter by category - all categories use standard filtering now
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(term) ||
        (item.code && item.code.toLowerCase().includes(term)) ||
        (item.description && item.description.toLowerCase().includes(term))
      );
    }

    setFilteredItems(filtered);
  };

  const handleToggleActive = (item: CatalogItem) => {
    if (!user?.practice_id) return;
    setItemToToggle(item);
    setShowToggleConfirm(true);
  };

  const confirmToggleActive = async () => {
    if (!user?.practice_id || !itemToToggle) return;

    try {
      // Toggle is_active status using update
      await updateCatalogItem(user.practice_id, itemToToggle.id, { 
        is_active: !itemToToggle.is_active 
      });
      await fetchItems();
    } catch (err: any) {
      console.error('Failed to toggle item:', err);
      alert('Failed to update item: ' + err.message);
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'Diagnostic': return 'bg-blue-100 text-blue-800';
      case 'Treatment': return 'bg-green-100 text-green-800';
      case 'Medication': return 'bg-purple-100 text-purple-800';
      case 'Product': return 'bg-orange-100 text-orange-800';
      case 'Labs': return 'bg-teal-100 text-teal-800';
      case 'Immunizations': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Billing Catalog</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage your practice's billable items and services
            </p>
          </div>
          {/* Add Item Dropdown - Links to clinical catalogs */}
          <div className="relative" ref={addMenuRef}>
            <button
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Item
              <ChevronDown className={`w-4 h-4 transition-transform ${showAddMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {showAddMenu && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-50 py-1">
                <Link
                  to="/settings/immunization-catalog"
                  onClick={() => setShowAddMenu(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Immunization
                </Link>
                <Link
                  to="/settings/labs-catalog"
                  onClick={() => setShowAddMenu(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Lab
                </Link>
                <Link
                  to="/settings/diagnostics-catalog"
                  onClick={() => setShowAddMenu(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Diagnostic
                </Link>
                <Link
                  to="/settings/medications-catalog"
                  onClick={() => setShowAddMenu(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Medication
                </Link>
                <Link
                  to="/settings/treatments-catalog"
                  onClick={() => setShowAddMenu(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Treatment
                </Link>
                <Link
                  to="/settings/products-catalog"
                  onClick={() => setShowAddMenu(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Product
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedCategory === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Filter Dropdown - Below Search */}
          <div className="relative mt-3" ref={filterMenuRef}>
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className="p-2 rounded-lg border bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors"
              title="Filter options"
            >
              <Filter className="w-5 h-5" />
            </button>
            
            {showFilterMenu && (
              <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 min-w-48">
                <button
                  onClick={() => {
                    handleShowInactiveChange(!showInactive);
                    setShowFilterMenu(false);
                  }}
                  className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:bg-gray-700 rounded-lg"
                >
                  {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No items found</p>
                      <p className="text-sm mt-1">Try adjusting your filters or add a new item</p>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr 
                      key={item.id} 
                      className={`hover:bg-gray-50 cursor-pointer ${!item.is_active ? 'opacity-50' : ''}`}
                      onClick={() => navigate(`/settings/catalog/${item.id}/edit`)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.name}</div>
                        {item.code && <div className="text-xs text-gray-500">{item.code}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryBadgeColor(item.category)}`}>
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        ${item.unit_price.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.measure_unit || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActive(item);
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            item.is_active 
                              ? 'bg-green-500 focus:ring-green-500' 
                              : 'bg-gray-300 focus:ring-gray-400'
                          }`}
                          title={item.is_active ? 'Click to deactivate' : 'Click to activate'}
                          role="switch"
                          aria-checked={item.is_active}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                              item.is_active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredItems.length} of {items.length} items
        </div>
      </div>

      {/* Toggle Active/Inactive Confirmation */}
      <ConfirmDialog
        isOpen={showToggleConfirm}
        onClose={() => {
          setShowToggleConfirm(false);
          setItemToToggle(null);
        }}
        onConfirm={confirmToggleActive}
        title={itemToToggle?.is_active ? 'Deactivate Item?' : 'Activate Item?'}
        message={
          itemToToggle?.is_active
            ? `Are you sure you want to deactivate "${itemToToggle?.name}"? It will no longer appear in order lists.`
            : `Are you sure you want to activate "${itemToToggle?.name}"? It will be available for orders again.`
        }
        confirmText={itemToToggle?.is_active ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        isDangerous={itemToToggle?.is_active === true}
      />
    </div>
  );
};

export default CatalogManagement;

